/**
 * CSV Parser - Utilities for parsing pipe-delimited CSV with section headers
 * 
 * Format:
 * ===SECTION_NAME===
 * col1|col2|col3
 * val1|val2|val3
 * 
 * Delimiters:
 * - `|` for columns
 * - `;` for arrays within a column
 * - `@` for sub-fields within a value
 */

export interface CSVSection {
    name: string;
    headers: string[];
    rows: Record<string, string>[];
}

/**
 * Split CSV text into sections by ===HEADER===
 * Also handles fallback when markers are missing (detects by header patterns)
 */
export const splitSections = (csv: string): Map<string, string> => {
    const sections = new Map<string, string>();
    const sectionRegex = /===(\w+)===/g;
    const parts = csv.split(sectionRegex);

    // parts: ["", "SECTION1", "content1", "SECTION2", "content2", ...]
    for (let i = 1; i < parts.length; i += 2) {
        const sectionName = parts[i].trim();
        const sectionContent = parts[i + 1]?.trim() || '';
        sections.set(sectionName, sectionContent);
    }

    // FALLBACK: If SCORES or SEGMENTS missing, try to detect by header patterns
    if (!sections.has('SCORES')) {
        const scoresMatch = csv.match(/overall\|accuracy\|terminology\|tone\s*\n([^\n]+)/i);
        if (scoresMatch) {
            sections.set('SCORES', `overall|accuracy|terminology|tone\n${scoresMatch[1]}`);
        }
    }

    if (!sections.has('SEGMENTS')) {
        const segmentsMatch = csv.match(/sourceIndex\|humanIndices\|score\|rules\|errors\|reviews\|notes\s*\n([\s\S]*?)(?=\n===|$)/i);
        if (segmentsMatch) {
            sections.set('SEGMENTS', `sourceIndex|humanIndices|score|rules|errors|reviews|notes\n${segmentsMatch[1].trim()}`);
        }
    }

    return sections;
};

/**
 * Parse a single section content into rows
 */
export const parseSection = (content: string): CSVSection | null => {
    const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 1) return null;

    const headers = lines[0].split('|').map(h => h.trim());
    const rows: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split('|');
        const row: Record<string, string> = {};

        headers.forEach((header, idx) => {
            row[header] = values[idx]?.trim() || '';
        });

        rows.push(row);
    }

    return { name: '', headers, rows };
};

/**
 * Parse semicolon-separated values into array
 */
export const parseArray = (value: string): string[] => {
    if (!value || value === '') return [];
    return value.split(';').map(v => v.trim()).filter(Boolean);
};

/**
 * Parse @ separated sub-fields
 */
export const parseSubFields = (value: string): string[] => {
    if (!value || value === '') return [];
    return value.split('@').map(v => v.trim());
};

/**
 * Parse number from string, return 0 if invalid
 */
export const parseNumber = (value: string): number => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
};

/**
 * Parse humanIndices from semicolon-separated numbers
 * Returns empty array for non-numeric values
 */
export const parseNumberArray = (value: string): number[] => {
    return parseArray(value).map(v => parseInt(v, 10)).filter(n => !isNaN(n));
};

/**
 * Parse humanIndices with MISSING and special format support
 * Returns { isMissing: boolean, isPartialMatch: boolean, indices: number[], rawValue: string }
 */
export interface HumanIndicesResult {
    isMissing: boolean;
    isPartialMatch: boolean;
    indices: number[];
    rawValue: string;
}

export const parseHumanIndices = (value: string): HumanIndicesResult => {
    const trimmed = (value || '').trim().toUpperCase();

    // Check for MISSING keyword
    if (trimmed === 'MISSING' || trimmed === '') {
        return { isMissing: true, isPartialMatch: false, indices: [], rawValue: value };
    }

    // Check for "part of X" format
    const partMatch = value.match(/part\s+of\s+(\d+)/i);
    if (partMatch) {
        const idx = parseInt(partMatch[1], 10);
        return {
            isMissing: false,
            isPartialMatch: true,
            indices: isNaN(idx) ? [] : [idx],
            rawValue: value
        };
    }

    // Normal case: parse numbers
    const indices = parseNumberArray(value);
    return {
        isMissing: indices.length === 0,
        isPartialMatch: false,
        indices,
        rawValue: value
    };
};

